import { Bookmark } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { giveAskHeading, type GiveAskDirectoryItem } from '@/lib/give-ask-api';

interface GiveAskCardProps {
  item: GiveAskDirectoryItem;
  isOwn: boolean;
  onBookmark: () => void;
  onReply: () => void;
  onFulfil: () => void;
  onReplies: () => void;
}

export function GiveAskCard({ item, isOwn, onBookmark, onReply, onFulfil, onReplies }: GiveAskCardProps) {
  const ownerName = [item.owner.firstName, item.owner.lastName].filter(Boolean).join(' ') || 'Member';
  const fulfilled = item.status === 'FULFILLED';
  const isAsk = item.type === 'ASK';
  const categoryLabel = item.category
    ? [item.category, item.subCategory].filter(Boolean).join(' > ')
    : null;

  return (
    <article className="flex flex-col gap-3 rounded-2xl border bg-white p-5 shadow-sm">
      <header className="flex items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-2 text-xs font-semibold uppercase tracking-wide">
            <span className={isAsk ? 'text-orange-600' : 'text-teal-700'}>{isAsk ? 'Ask' : 'Give'}</span>
            {fulfilled ? <span className="text-emerald-700">Fulfilled</span> : null}
          </div>
          <h3 className="font-semibold text-[var(--navy)]">{giveAskHeading(item)}</h3>
          <p className="text-sm text-muted-foreground">
            {ownerName}
            {item.businessName ? ` · ${item.businessName}` : ''}
          </p>
        </div>
        <button
          type="button"
          onClick={onBookmark}
          aria-label={item.bookmarked ? 'Remove from saved' : 'Save'}
          aria-pressed={item.bookmarked}
          className="shrink-0 rounded-full p-2 text-muted-foreground hover:bg-slate-100"
        >
          <Bookmark className={item.bookmarked ? 'h-5 w-5 fill-current text-orange-600' : 'h-5 w-5'} />
        </button>
      </header>

      <p className="whitespace-pre-line text-sm text-[var(--navy)] line-clamp-4">{item.description}</p>

      {categoryLabel ? <p className="text-xs text-muted-foreground">{categoryLabel}</p> : null}

      <footer className="flex flex-wrap gap-2">
        {!isOwn && isAsk && !fulfilled ? (
          <Button size="sm" onClick={onReply} disabled={item.hasReplied}>
            {item.hasReplied ? 'Replied' : 'Reply with a lead'}
          </Button>
        ) : null}
        {isOwn && isAsk && !fulfilled ? (
          <Button size="sm" variant="outline" onClick={onFulfil}>
            Mark fulfilled
          </Button>
        ) : null}
        {isOwn && isAsk ? (
          <Button size="sm" variant="ghost" onClick={onReplies}>
            Replies
          </Button>
        ) : null}
      </footer>
    </article>
  );
}
